/**
 * api.js — License Portal API client.
 *
 * All portal endpoints communicate with the License API Worker.
 * Portal-specific routes require an X-Portal-Token header (HMAC session token).
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://api.hforgex.com';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

class ApiError extends Error {
    /** @param {number} status @param {string} error @param {string} [message] */
    constructor(status, error, message) {
        super(message || error);
        this.name = 'ApiError';
        this.status = status;
        this.error = error;
    }
}

/**
 * @param {string} path
 * @param {object} [options]
 * @param {object} [options.body]
 * @param {string} [options.token]
 * @param {'GET'|'POST'} [options.method]
 * @returns {Promise<any>}
 */
async function request(path, { body, token, method } = {}) {
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
        headers['X-Portal-Token'] = token;
    }

    const httpMethod = method || (body ? 'POST' : 'GET');

    const fetchOpts = { method: httpMethod, headers };
    if (body) {
        fetchOpts.body = JSON.stringify(body);
    }

    const res = await fetch(`${API_BASE}${path}`, fetchOpts);

    const data = await res.json();

    if (!res.ok) {
        throw new ApiError(res.status, data.error, data.message);
    }

    return data;
}

// ---------------------------------------------------------------------------
// Public API — Portal endpoints
// ---------------------------------------------------------------------------

/**
 * Look up a license by email + license key.
 * @param {string} email
 * @param {string} licenseKey
 * @returns {Promise<{ licenseId: string, productId: string, edition: string, maintenanceEnd: string, maxDevices: number, status: string, devices: Array, sessionToken: string }>}
 */
export async function lookupLicense(email, licenseKey) {
    return request('/v1/portal/lookup', {
        body: { email, licenseKey },
    });
}

/**
 * Register a device on a license.
 * @param {string} token     Portal session token
 * @param {string} licenseId
 * @param {string} deviceCode  UUIDv4
 * @param {string} [deviceName]
 * @returns {Promise<{ ok: boolean, deviceCode: string }>}
 */
export async function addDevice(token, licenseId, deviceCode, deviceName) {
    return request('/v1/portal/add-device', {
        token,
        body: { licenseId, deviceCode, deviceName: deviceName || undefined },
    });
}

/**
 * Remove a device from a license.
 * @param {string} token
 * @param {string} licenseId
 * @param {string} deviceCode
 * @returns {Promise<{ ok: boolean }>}
 */
export async function removeDevice(token, licenseId, deviceCode) {
    return request('/v1/portal/remove-device', {
        token,
        body: { licenseId, deviceCode },
    });
}

/**
 * Generate and return a signed license bundle.
 * @param {string} token
 * @param {string} licenseId
 * @returns {Promise<{ bundle: object, bundleText: string }>}
 */
export async function issueBundle(token, licenseId) {
    return request('/v1/portal/issue-bundle', {
        token,
        body: { licenseId },
    });
}

/**
 * Extend maintenance_end by 1 year.
 * @param {string} token
 * @param {string} licenseId
 * @returns {Promise<{ ok: boolean, maintenanceEnd: string }>}
 */
export async function renewLicense(token, licenseId) {
    return request('/v1/portal/renew', {
        token,
        body: { licenseId },
    });
}

// ---------------------------------------------------------------------------
// Public API — Free upgrade endpoints
// ---------------------------------------------------------------------------

/**
 * Request a verification code for a free product upgrade.
 * @param {string} email
 * @param {string} productId
 * @returns {Promise<{ ok: boolean, message: string }>}
 */
export async function requestFreeUpgrade(email, productId) {
    return request('/v1/free/request-upgrade', {
        body: { email, productId },
    });
}

/**
 * Confirm a free upgrade with the 6-digit code.
 * @param {string} email
 * @param {string} code
 * @param {string} productId
 * @returns {Promise<{ ok: boolean, licenseId: string }>}
 */
export async function confirmFreeUpgrade(email, code, productId) {
    return request('/v1/free/confirm-upgrade', {
        body: { email, code, productId },
    });
}

// ---------------------------------------------------------------------------
// Public API — Product catalog & order polling
// ---------------------------------------------------------------------------

/**
 * Fetch available product catalog.
 * @returns {Promise<Array<{ id: string, slug: string, name: string, price: number, currency: string, isFree: boolean }>>}
 */
export async function fetchProducts() {
    return request('/v1/products');
}

/**
 * Poll for order creation after external checkout.
 * Returns { status: 'pending' } until the webhook creates the license,
 * then { status: 'ready', licenseId, sessionToken, ... }.
 * @param {string} email
 * @param {string} orderId
 * @returns {Promise<{ status: 'pending' | 'ready', licenseId?: string, edition?: string, maintenanceEnd?: string, maxDevices?: number, licenseStatus?: string, devices?: Array, sessionToken?: string }>}
 */
export async function pollOrderStatus(email, orderId) {
    const params = new URLSearchParams({ email, orderId });
    return request(`/v1/portal/order-status?${params}`);
}

/**
 * Poll for paid purchase fulfillment after hosted checkout.
 * Does not return the license key; the key is delivered by email.
 * @param {string} email
 * @param {string} productId
 * @param {string} [activationClaim]
 * @returns {Promise<{ status: 'pending' | 'ready', productId?: string, orderCreatedAt?: string, licenseIssuedAt?: string, licenseId?: string, sessionToken?: string, devices?: Array }>}
 */
export async function pollPurchaseStatus(email, productId, activationClaim = '') {
    const params = new URLSearchParams({ email, productId });
    if (activationClaim) params.set('activationClaim', activationClaim);
    return request(`/v1/portal/purchase-status?${params}`);
}

export { ApiError };
